import React, { memo } from 'react';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import type { HazmatInfo } from '../../types/label';
import FormField from './FormField';
import SelectInput from './SelectInput';
import {
  HAZARD_CLASSES,
  HAZMAT_CATEGORIES,
  HAZMAT_CONTAINER_TYPES,
  HAZMAT_PACKING_GROUPS,
  HAZMAT_QUANTITY_TYPES,
  HAZMAT_QUANTITY_UNITS,
  HAZMAT_TRANSPORT_MODES,
  isBlank,
} from './labelForm';
import { denseInputSx, numericInputProps } from './styles';

interface HazmatFieldsProps {
  info: HazmatInfo;
  onChange: (patch: Partial<HazmatInfo>) => void;
}

/** Hazmat detail form; the "defined" category exposes the full DOT/IATA fields. */
const HazmatFields: React.FC<HazmatFieldsProps> = ({ info, onChange }) => (
  <Box
    role="group"
    aria-label="Hazmat details"
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))',
      gap: 1.5,
      p: 1.5,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <FormField label="Hazmat category" required>
      <SelectInput dense label="Hazmat category" value={info.category} options={HAZMAT_CATEGORIES} onChange={(category) => onChange({ category })} />
    </FormField>
    <FormField label="Quantity" required>
      <OutlinedInput
        type="number"
        value={Number.isNaN(info.quantity) ? '' : info.quantity}
        onChange={(e) => onChange({ quantity: parseFloat(e.target.value) })}
        inputProps={{ ...numericInputProps, step: 0.1, min: 0 }}
        sx={denseInputSx}
      />
    </FormField>
    <FormField label="Quantity type">
      <SelectInput dense label="Quantity type" value={info.quantityType} options={HAZMAT_QUANTITY_TYPES} onChange={(quantityType) => onChange({ quantityType })} />
    </FormField>
    <FormField label="Quantity units">
      <SelectInput dense label="Quantity units" value={info.quantityUnits} options={HAZMAT_QUANTITY_UNITS} onChange={(quantityUnits) => onChange({ quantityUnits })} />
    </FormField>
    <FormField label="Container type">
      <SelectInput dense label="Container type" value={info.containerType} options={HAZMAT_CONTAINER_TYPES} onChange={(containerType) => onChange({ containerType })} />
    </FormField>

    {info.category === 'defined' && (
      <>
        <FormField label="Hazmat ID (UN number)" required>
          <OutlinedInput
            value={info.hazmatId || ''}
            placeholder="e.g. UN1755"
            error={isBlank(info.hazmatId)}
            onChange={(e) => onChange({ hazmatId: e.target.value })}
            sx={denseInputSx}
          />
        </FormField>
        <FormField label="Proper shipping name" required>
          <OutlinedInput
            value={info.properShippingName || ''}
            error={isBlank(info.properShippingName)}
            onChange={(e) => onChange({ properShippingName: e.target.value })}
            sx={denseInputSx}
          />
        </FormField>
        <FormField label="Packing group">
          <SelectInput dense label="Packing group" value={info.packingGroup || ''} placeholder="Select" options={HAZMAT_PACKING_GROUPS} onChange={(packingGroup) => onChange({ packingGroup })} />
        </FormField>
        <FormField label="Transport mode">
          <SelectInput dense label="Transport mode" value={info.transportMode || ''} placeholder="Select" options={HAZMAT_TRANSPORT_MODES} onChange={(transportMode) => onChange({ transportMode })} />
        </FormField>
        <FormField label="Packing instruction code">
          <OutlinedInput value={info.packingInstructionCode || ''} onChange={(e) => onChange({ packingInstructionCode: e.target.value })} sx={denseInputSx} />
        </FormField>
        <FormField label="Hazard class">
          <SelectInput dense label="Hazard class" value={info.hazardClass || ''} placeholder="Select" options={HAZARD_CLASSES} onChange={(hazardClass) => onChange({ hazardClass })} />
        </FormField>
      </>
    )}
  </Box>
);

export default memo(HazmatFields);
